import React, { FC, ReactNode, useEffect } from 'react'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import { StringParam } from 'use-query-params'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { LOAD_TABS } from 'apps/fleet-planner/constants'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ITodo } from 'apps/fleet-planner/types'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { useQueryParams } from 'utils/hooks'
import { TodosInlineMessage } from '../Dashboard/Todos/TodosInlineMessage'
import { FMS_ELD_ERRORS } from '../eld-tracking/ELDErrors'
import { getELDError } from '../eld-tracking/utils'
import { UploadPodAlert } from './UploadPodAlert'

interface IAlert {
  message: ReactNode
  type: INLINE_MESSAGE_VARIANTS
}

type TAlertKeyName = 'addDriverAlert'

const ALERTS: Record<TAlertKeyName, IAlert> = {
  addDriverAlert: {
    type: INLINE_MESSAGE_VARIANTS.WARNING,
    message: 'This load is missing a driver. Please add a driver below',
  },
}

const getModalMessage = (keyName: string): IAlert | undefined => {
  return get(ALERTS, keyName, undefined)
}

interface ILoadDetailsAlertProps {
  onTodoClick: (name?: string, elementId?: string) => void
  onPODUpload: (newTab: LOAD_TABS) => void
  currentShipmentTodos?: ITodo
}

/**
 * Banner area that loads on top of the <LoadDetailsModal /> and <LoadDetails /> page
 */
export const LoadDetailsAlerts: FC<ILoadDetailsAlertProps> = ({
  currentShipmentTodos,
  onPODUpload,
  onTodoClick,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const movePodWarningToTopInCarrierAppEnabled = useFeatureFlag('movePodWarningToTopInCarrierApp')

  const { load } = useSelector(loadDetailsSelector)
  const { isExternal, motorCarrier, trackingMethod, truck } = load || {}
  const { isKeepTruckinEnabled } = motorCarrier || {}

  const { shipmentDetails } = useMarketplaceContext()
  const { project44Metadata } = shipmentDetails || {}
  const {
    isCarrierProject44Enabled,
    isTruckNumberValid: p44IsTruckNumberValid,
    needsValidTruckNumber: p44NeedsValidTruckNumber,
  } = project44Metadata || {}

  const eldTrackingError = getELDError({
    isKeepTruckinEnabled,
    isP44Enabled: isCarrierProject44Enabled,
    p44IsTruckNumberValid,
    p44NeedsValidTruckNumber,
    trackingMethod,
    truck,
  })

  // Mixpanel events for keepTruckin, p44, and mobile error alerts.
  useEffect(() => {
    if (
      eldTrackingError === FMS_ELD_ERRORS.keepTruckinTruckNotEntered ||
      eldTrackingError === FMS_ELD_ERRORS.p44TruckNotEntered
    ) {
      trackEvent('ELD', 'VIEWED_TRUCK_NOT_FOUND_ERROR')
    } else if (eldTrackingError === FMS_ELD_ERRORS.mobileDriverIsntTracking) {
      trackEvent('ELD', 'VIEWED_MOBILE_NOT_TRACKING_ERROR')
    } else if (eldTrackingError === FMS_ELD_ERRORS.keepTruckinCarrierNotConnected) {
      trackEvent('ELD', 'VIEWED_NOT_CONNECTED_TO_KEEP_TRUCKING_ERROR')
    } else if (eldTrackingError === FMS_ELD_ERRORS.keepTruckinTruckNotFound) {
      trackEvent('ELD', 'VIEWED_TRUCK_NOT_FOUND_IN_KEEP_TRUCKING_ERROR')
    } else if (eldTrackingError === FMS_ELD_ERRORS.p44CarrierNotConnected) {
      trackEvent('ELD', 'VIEWED_NOT_CONNECTED_TO_P44_ERROR')
    } else if (eldTrackingError === FMS_ELD_ERRORS.p44TruckNotFound) {
      trackEvent('ELD', 'VIEWED_TRUCK_NOT_FOUND_IN_P44_ERROR')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eldTrackingError, trackingMethod])

  const [{ modalMessageKey }] = useQueryParams({
    modalMessageKey: StringParam,
  })
  const { podMissing, podRejected } = currentShipmentTodos || {}

  const modalAlert = getModalMessage(modalMessageKey)

  const hasAlerts = !!modalAlert?.message
  const hasPODInlineMessage = podMissing || podRejected || isExternal

  return (
    <Box mt={hasAlerts ? 3 : 0}>
      {modalAlert?.message && (
        <Box mb={1}>
          <InlineMessage message={modalAlert?.message} type={modalAlert?.type} />
        </Box>
      )}
      <Box mt={1}>
        <TodosInlineMessage
          onClick={onTodoClick}
          load={load}
          currentShipmentTodos={currentShipmentTodos}
        />
      </Box>
      {hasPODInlineMessage && movePodWarningToTopInCarrierAppEnabled && (
        <Box mt={1}>
          <UploadPodAlert onUpload={onPODUpload} load={load} />
        </Box>
      )}
      {!!eldTrackingError && (
        <Box mt={1}>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={eldTrackingError.message} />
        </Box>
      )}
    </Box>
  )
}
